import React, { useEffect, useMemo, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { getNextInvoiceNumber } from '../utils/invoiceNumber';
import { buildQuickSalePayload, parseQuickSaleAmount } from '../utils/quickSale';
import { formatMoney } from '../utils/money';
import ClienteSearchField from './ClienteSearchField';
import PlanUsageBanner from './PlanUsageBanner';
import { CATEGORIAS_VENTA } from '../pages/facturas/facturasPageUtils';
import { FormAlert, FormField, formInputClass } from './form';

const CONCEPT_TO_CATEGORY = {
  Honorarios: 'Honorarios',
  'Asesoría mensual': 'Asesoría mensual',
  'Proyecto / entrega': 'Proyecto único',
  'Venta de producto': 'Otro',
  'Servicio puntual': 'Otro',
};

const TODAY = () => new Date().toISOString().slice(0, 10);

const CONCEPT_SUGGESTIONS = [
  'Honorarios',
  'Asesoría mensual',
  'Proyecto / entrega',
  'Venta de producto',
  'Servicio puntual',
];

const PAYMENT_METHODS = ['Efectivo', 'Transferencia', 'Mercado Pago', 'Tarjeta', 'Cheque', 'Otro'];

function defaultVencimientoOnboarding() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

function addDaysFromIso(isoDate, days) {
  const base = isoDate || TODAY();
  const [y, m, d] = base.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function getInitialState(preferImpagaOnboarding) {
  return {
    concepto: '',
    monto: '',
    fecha: TODAY(),
    estadoPago: preferImpagaOnboarding ? 'impaga' : 'pagada',
    vencimiento: preferImpagaOnboarding ? defaultVencimientoOnboarding() : '',
    formaPago: 'Transferencia',
    cliente: '',
    categoria: '',
  };
}

export default function VentaRapidaForm({
  onAdd,
  modoOscuro,
  existingInvoices = [],
  preferImpagaOnboarding = false,
  formResetKey = 0,
  onRequestCompleteMode,
  embedded = false,
}) {
  const { secureFetch } = useApi();
  const tc = useThemeClasses(modoOscuro);
  const [fields, setFields] = useState(() => getInitialState(preferImpagaOnboarding));
  const [clientes, setClientes] = useState([]);
  const [selectedClientRecord, setSelectedClientRecord] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    concepto,
    monto,
    fecha,
    estadoPago,
    vencimiento,
    formaPago,
    cliente,
    categoria,
  } = fields;

  const setField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const numero = useMemo(
    () => getNextInvoiceNumber(existingInvoices),
    [existingInvoices],
  );

  const parsedAmount = parseQuickSaleAmount(monto);
  const canSubmit = concepto.trim().length > 0 && parsedAmount > 0 && Boolean(fecha)
    && (estadoPago !== 'impaga' || (vencimiento && vencimiento >= fecha));

  useEffect(() => {
    setFields(getInitialState(preferImpagaOnboarding));
    setSelectedClientRecord(null);
    setError('');
  }, [formResetKey, preferImpagaOnboarding]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await secureFetch('/api/clients');
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled && Array.isArray(data)) setClientes(data);
      } catch {
        // La venta rápida puede registrarse sin un cliente creado.
      }
    })();
    return () => { cancelled = true; };
  }, [secureFetch]);

  const input = formInputClass(tc.inputClass);

  const selectedClient = useMemo(() => {
    if (selectedClientRecord) return selectedClientRecord;
    const customerName = cliente.trim().toLowerCase();
    if (!customerName) return null;
    return clientes.find((item) =>
      String(item.razonSocial || item.name || '').trim().toLowerCase() === customerName
    ) || null;
  }, [cliente, clientes, selectedClientRecord]);

  const contactLinked = Boolean(
    selectedClient?.email || selectedClient?.telefono || selectedClient?.phone
  );

  const handleClienteChange = (valor) => {
    setField('cliente', valor);
    setSelectedClientRecord(null);
  };

  const handleClienteSelect = (record) => {
    setSelectedClientRecord(record);
    setField('cliente', record.razonSocial || record.name || '');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || !canSubmit) return;
    setError('');

    const amount = parsedAmount;
    if (estadoPago === 'impaga' && !vencimiento) {
      setError('Indicá cuándo esperás cobrar la venta pendiente.');
      return;
    }
    if (estadoPago === 'impaga' && vencimiento < fecha) {
      setError('El vencimiento no puede ser anterior a la fecha de venta.');
      return;
    }
    if (typeof onAdd !== 'function') {
      setError('No se pudo procesar la venta. Actualizá la página e intentá nuevamente.');
      return;
    }

    setSubmitting(true);
    try {
      const ok = await onAdd(buildQuickSalePayload({
        numero,
        fecha,
        cliente,
        concepto,
        monto: amount,
        estadoPago,
        formaPago,
        vencimiento,
        clienteData: selectedClient,
        categoria,
      }));
      if (ok === false) {
        setError('No se pudo guardar la venta. Revisá los datos e intentá de nuevo.');
      }
    } catch (submitError) {
      setError(submitError?.message || 'No se pudo guardar la venta.');
    } finally {
      setSubmitting(false);
    }
  };

  const pad = embedded ? 'px-5 sm:px-6 py-4 sm:py-5' : 'p-4 sm:p-6';

  const submitButton = (
    <button
      type="submit"
      disabled={submitting || !canSubmit}
      className={`w-full sm:w-auto shrink-0 rounded-xl px-8 py-3 text-sm font-semibold transition-colors disabled:opacity-45 ${tc.btnPrimary}`}
    >
      {submitting ? 'Guardando…' : 'Registrar venta'}
    </button>
  );

  return (
    <form onSubmit={handleSubmit} className="relative max-sm:pb-[4.75rem]">
      <div className={pad}>
        <FormAlert type="error" className="mb-3">{error}</FormAlert>

        {preferImpagaOnboarding && (
          <p className={`mb-3 rounded-lg border px-3 py-2 text-xs ${tc.section}`}>
            Para ver quién te debe en Cobrar, dejá la venta como <strong>pendiente</strong> (sugerimos cobro en una semana).
          </p>
        )}

        <div className="lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
          <div className={`flex flex-col gap-3 lg:col-span-4 lg:justify-start ${embedded ? `lg:border-r lg:pr-8 ${tc.divide}` : ''}`}>
            <div>
              <p className={`text-xs font-medium mb-2 ${tc.labelClass}`}>Estado del cobro</p>
              <div className={`grid grid-cols-2 gap-1.5 rounded-xl border p-1 ${tc.section}`}>
                {[
                  { id: 'pagada', label: 'Ya cobré' },
                  { id: 'impaga', label: 'Me deben' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={estadoPago === option.id}
                    onClick={() => {
                      setField('estadoPago', option.id);
                      if (option.id === 'pagada') {
                        setField('vencimiento', '');
                      } else if (!vencimiento) {
                        setField('vencimiento', addDaysFromIso(fecha, 7));
                      }
                    }}
                    className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                      estadoPago === option.id
                        ? option.id === 'impaga'
                          ? 'bg-amber-500 text-white'
                          : 'bg-emerald-600 text-white'
                        : tc.tabInactive
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={`block text-xs font-medium mb-2 ${tc.labelClass}`} htmlFor="venta-rapida-monto">
                Importe total (ARS)
              </label>
              <div className="relative">
                <span className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-xl font-medium ${tc.textMuted}`}>
                  $
                </span>
                <input
                  id="venta-rapida-monto"
                  type="text"
                  inputMode="decimal"
                  value={monto}
                  onChange={(event) => setField('monto', event.target.value)}
                  className={`${input} text-2xl sm:text-3xl font-bold tracking-tight py-3 pl-10 pr-4`}
                  placeholder="0"
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <p className={`mt-1.5 text-xs ${tc.textMuted}`}>
                Sin IVA · registro interno N.º {numero}
              </p>
            </div>

            <div className={`rounded-xl border px-3 py-2.5 ${tc.section}`}>
              <PlanUsageBanner modoOscuro={modoOscuro} variant="inline" embedded />
            </div>
          </div>

          <div className="mt-5 space-y-5 lg:col-span-8 lg:mt-0">
              <section className="space-y-3">
                <FormField label="¿Qué vendiste?" required labelClass={tc.labelClass}>
                  <input
                    type="text"
                    value={concepto}
                    onChange={(event) => setField('concepto', event.target.value)}
                    className={input}
                    placeholder="Ej. honorarios marzo, entrega de proyecto…"
                    autoComplete="off"
                  />
                </FormField>
                <div className="flex flex-wrap gap-1.5">
                  {CONCEPT_SUGGESTIONS.map((label) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => {
                        setFields((prev) => ({
                          ...prev,
                          concepto: label,
                          categoria: CONCEPT_TO_CATEGORY[label] || prev.categoria,
                        }));
                      }}
                      className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                        concepto === label
                          ? 'border-[#1F80FF] bg-[#1F80FF]/10 text-[#1F80FF]'
                          : `${tc.section} ${tc.textSec}`
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <FormField
                  label="Categoría"
                  labelClass={tc.labelClass}
                  textMuted={tc.textMuted}
                  hint="Para reportes y filtros en Mis ventas."
                >
                  <select
                    value={categoria}
                    onChange={(event) => setField('categoria', event.target.value)}
                    className={input}
                  >
                    <option value="">Sin categoría</option>
                    {CATEGORIAS_VENTA.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </FormField>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIAS_VENTA.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setField('categoria', cat)}
                      className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                        categoria === cat
                          ? 'border-[#1F80FF] bg-[#1F80FF]/10 text-[#1F80FF]'
                          : `${tc.section} ${tc.textSec}`
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </section>

              <section className={`space-y-3 border-t pt-5 ${tc.divide}`}>
                <ClienteSearchField
                  id="venta-rapida-cliente"
                  value={cliente}
                  onChange={handleClienteChange}
                  onSelect={handleClienteSelect}
                  clientes={clientes}
                  inputClass={input}
                  boxClass={tc.box}
                  divideClass={tc.divide}
                  textPri={tc.textPri}
                  textMuted={tc.textMuted}
                  labelClass={tc.labelClass}
                  label="Cliente"
                  placeholder="Buscar o escribir cliente…"
                  hint="Opcional. Vacío = Consumidor final."
                />
                {contactLinked && (
                  <p className={`text-[11px] ${modoOscuro ? 'text-green-300' : 'text-green-700'}`}>
                    Contacto vinculado para Cobranzas.
                  </p>
                )}
              </section>

              <section className={`space-y-3 border-t pt-5 ${tc.divide}`}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Fecha" required labelClass={tc.labelClass}>
                <input
                  type="date"
                  value={fecha}
                  onChange={(event) => setField('fecha', event.target.value)}
                  className={input}
                />
              </FormField>
              <FormField label="Medio de pago" labelClass={tc.labelClass}>
                <select
                  value={formaPago}
                  onChange={(event) => setField('formaPago', event.target.value)}
                  className={input}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </FormField>
            </div>

            {estadoPago === 'impaga' && (
              <div className="space-y-3 pt-1">
                <p className={`text-xs font-medium ${tc.labelClass}`}>Vencimiento</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { days: 7, label: '7 días' },
                    { days: 15, label: '15 días' },
                    { days: 30, label: '30 días' },
                    { days: 45, label: '45 días' },
                  ].map(({ days, label }) => {
                    const target = addDaysFromIso(fecha, days);
                    const active = vencimiento === target;
                    return (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setField('vencimiento', target)}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                          active
                            ? 'border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-200'
                            : `${tc.section} ${tc.textSec}`
                        }`}
                      >
                        <FiCalendar className="h-3 w-3" />
                        {label}
                      </button>
                    );
                  })}
                </div>
                <input
                  type="date"
                  min={fecha}
                  value={vencimiento}
                  onChange={(event) => setField('vencimiento', event.target.value)}
                  className={input}
                />
              </div>
            )}
              </section>
          </div>
        </div>

        <div className={`mt-5 hidden items-center justify-between gap-4 border-t pt-4 lg:flex ${tc.divide}`}>
          <p className={`text-xs leading-relaxed ${tc.textMuted}`}>
            No reemplaza una factura fiscal de ARCA.
            {onRequestCompleteMode && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={onRequestCompleteMode}
                  className="font-medium text-[#1F80FF] hover:underline"
                >
                  Venta completa
                </button>
              </>
            )}
          </p>
          {submitButton}
        </div>
      </div>

      <div
        className={`fixed inset-x-0 bottom-0 z-30 border-t p-2.5 lg:hidden ${
          modoOscuro ? 'border-gray-700 bg-gray-900/95 backdrop-blur' : 'border-gray-200 bg-white/95 backdrop-blur'
        }`}
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className={`truncate text-[11px] ${tc.textMuted}`}>
              {concepto.trim() || 'Sin concepto'}
            </p>
            <p className={`text-base font-bold leading-tight ${tc.textPri}`}>
              {formatMoney(parsedAmount, 'ARS')}
            </p>
          </div>
          {submitButton}
        </div>
      </div>
    </form>
  );
}
